import axios from 'axios';

import { SubscriptionStatus } from '../models/Subscription';
import { PracticeStatus, AccessStatus } from '../types/practiceStatus';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { getPracticeProfile } from './practiceProfileAPI';
import { installSessionInterceptors } from './sessionInterceptors';
import { getSubscription } from './subscriptionsAPI';

const practiceStatusApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/practice`,
  withCredentials: true,
});

installSessionInterceptors(practiceStatusApi);

/**
 * Builds PracticeStatus from existing deployed endpoints (getPracticeProfile + getSubscription).
 * Used as fallback when the unified /api/practice/status endpoint is not yet deployed.
 */
async function buildStatusFromLegacyEndpoints(): Promise<PracticeStatus> {
  const [profile, subscription] = await Promise.allSettled([
    getPracticeProfile(),
    getSubscription(),
  ]);

  const prof = profile.status === 'fulfilled' ? profile.value : null;
  const sub = subscription.status === 'fulfilled' ? subscription.value : null;

  const now = new Date();
  const trialEnd = prof?.trialEndDate ? new Date(prof.trialEndDate) : new Date();
  const trialStart = prof?.trialStartDate ? new Date(prof.trialStartDate) : new Date();
  const isTrialExpired = trialEnd <= now;
  const trialDaysRemaining = Math.max(
    0,
    Math.ceil((trialEnd.getTime() - now.getTime()) / 86400000)
  );

  let accessStatus: AccessStatus = 'trial_active';
  let isActive = false;
  let hasPaidPlan = prof?.hasPaidPlan ?? false;
  let tier = 'trial';

  if (sub) {
    switch (sub.status) {
      case SubscriptionStatus.Active:
        accessStatus = 'paid_active';
        isActive = true;
        hasPaidPlan = true;
        tier = sub.tier !== undefined ? String(sub.tier) : 'launch';
        break;
      case SubscriptionStatus.PastDue:
        accessStatus = 'paid_past_due';
        isActive = true;
        hasPaidPlan = true;
        tier = sub.tier !== undefined ? String(sub.tier) : 'launch';
        break;
      case SubscriptionStatus.Cancelled:
        accessStatus = 'paid_canceled';
        isActive = false;
        hasPaidPlan = false;
        break;
      case SubscriptionStatus.Trial:
        if (isTrialExpired) {
          accessStatus = 'trial_expired';
          isActive = false;
        } else {
          accessStatus = 'trial_active';
          isActive = true;
        }
        break;
      case SubscriptionStatus.Expired:
        accessStatus = 'trial_expired';
        isActive = false;
        break;
      default:
        if (isTrialExpired) {
          accessStatus = 'trial_expired';
          isActive = false;
        } else {
          accessStatus = 'trial_active';
          isActive = true;
        }
    }
  } else {
    // No subscription data — use trial dates from profile
    isActive = !isTrialExpired;
    accessStatus = isTrialExpired ? 'trial_expired' : 'trial_active';
  }

  return {
    isActive,
    accessStatus,
    hasCompletedOnboarding: prof?.hasCompletedOnboarding ?? false,
    trialStartDate: trialStart.toISOString(),
    trialEndDate: trialEnd.toISOString(),
    trialDaysRemaining,
    isTrialExpired,
    tier,
    hasPaidPlan,
    currentPeriodStart: sub?.startDate ?? null,
    currentPeriodEnd: sub?.currentPeriodEnd ?? null,
    cancelAtPeriodEnd: false,
    practiceName: prof?.practiceName ?? null,
    clientNumbers: prof?.clientNumbers ?? null,
    teamSize: prof?.teamSize ?? null,
    practiceWebsite: prof?.practiceWebsite ?? null,
    entitlements: null,
  };
}

/**
 * Fetches the unified practice status.
 * Tries GET /api/practice/status first (new unified endpoint).
 * Falls back to constructing status from legacy endpoints if the new one isn't deployed.
 */
export const getPracticeStatus = async (): Promise<PracticeStatus> => {
  try {
    const response = await practiceStatusApi.get<PracticeStatus>('/status');

    return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
  } catch {
    // New endpoint not deployed yet — build from existing endpoints
    return buildStatusFromLegacyEndpoints();
  }
};
